import { normalizeTitle, validTitle } from './title-model';

export interface TaskRecord {
  id: number;
  title: string;
  completed: boolean;
}

export const initialTasks: TaskRecord[] = [
  { id: 1, title: 'Buy milk', completed: false },
  { id: 2, title: 'Finish assignment', completed: true },
  { id: 3, title: 'Call Alex', completed: false },
];

export function receiveChunks(chunks: string[], buffered: boolean) {
  if (!buffered) return { messages: chunks, pending: '' };
  const parts = chunks.join('').split('\n');
  return { messages: parts.slice(0, -1), pending: parts.at(-1) ?? '' };
}

export function executionTrace(tasks: TaskRecord[]) {
  const steps: {
    line: number;
    task: TaskRecord | null;
    count: number;
    output: string[];
    why: string;
  }[] = [];
  let count = 0;
  const output: string[] = [];
  const record = (line: number, task: TaskRecord | null, why: string) =>
    steps.push({ line, task, count, output: [...output], why });
  record(0, null, 'Assign 0 to open_count before inspecting any tasks.');
  for (const task of tasks) {
    record(1, task, `The loop binds task to record #${task.id}.`);
    record(
      2,
      task,
      `not ${task.completed ? 'True' : 'False'} is ${task.completed ? 'False: skip this block.' : 'True: enter this block.'}`,
    );
    if (!task.completed) {
      count += 1;
      record(3, task, 'Increase the count by one.');
      output.push(task.title);
      record(4, task, 'Print this open task’s title.');
    }
  }
  output.push(`Open: ${count}`);
  record(
    5,
    tasks.at(-1) ?? null,
    tasks.length
      ? 'The loop is finished. task still refers to its last record; print the final count once.'
      : 'The empty loop never assigned task. Print the final count once.',
  );
  return steps;
}

export function httpOutcome(method: string, path: string, body: string) {
  const fail = (status: number, reason: string, detail: string) => ({
    status,
    reason,
    payload: { detail },
    count: 1,
    decision: detail,
  });
  if (path !== '/tasks') return fail(404, 'Not Found', 'No route matches this path.');
  if (method === 'GET')
    return {
      status: 200,
      reason: 'OK',
      payload: [{ id: 1, title: 'Read HTTP', completed: false }],
      count: 1,
      decision: 'Read the task collection. No state changes.',
    };
  if (method !== 'POST') return fail(405, 'Method Not Allowed', 'This path supports GET and POST.');
  let value: unknown;
  try {
    value = JSON.parse(body);
  } catch {
    return fail(400, 'Bad Request', 'The body is not valid JSON.');
  }
  if (
    typeof value !== 'object' ||
    value === null ||
    !('title' in value) ||
    typeof value.title !== 'string' ||
    !validTitle(value.title)
  ) {
    return fail(400, 'Bad Request', 'A title must contain 1 to 80 characters.');
  }
  return {
    status: 201,
    reason: 'Created',
    payload: { id: 2, title: normalizeTitle(value.title), completed: false },
    count: 2,
    decision: 'Validate, create task #2 and save the new state.',
  };
}

export function wireResponse(result: ReturnType<typeof httpOutcome>) {
  const body = JSON.stringify(result.payload);
  return `HTTP/1.1 ${result.status} ${result.reason}\r\nContent-Type: application/json\r\nContent-Length: ${new TextEncoder().encode(body).length}\r\n${result.status === 405 ? 'Allow: GET, POST\r\n' : ''}Connection: close\r\n\r\n${body}`;
}
